import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { loginWithAccountCommunityUser, registerIndividual } from '../../common/fetch.js';
import {
  attachToAccountMsg,
  companyLinkedToElisaIdMsg,
  companyMsg,
  companySelectMsg,
  employeeOmaElisaMsg,
  goToOmaElisaMsg,
  informantBISMsg,
  linkedMsg,
  selectMsg,
  t,
} from '../../common/i18n/index.js';
import {
  FORM_GRID_FORCE_END,
  FORM_GRID_FULL_WIDTH,
  FormGridFieldset,
} from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset.js';
import { Email, PhoneNumber } from '../../common/react-hook-form/fields/index.js';
import { UserProfile } from '../../generated/api/userProfile.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { Combobox } from '../Combobox/Combobox.js';
import { GenericInfoDialog } from '../Dialogs/GenericInfoDialog.js';
import { Loading } from '../Loading/index.js';
import type { ContactSignatoryRights } from '../../generated/api/contactSignatoryRights.js';

import './SelectCompanySignatoryRight.scss';

export interface SelectCompanySignatoryRightProps {
  contactSignatoryRights: ContactSignatoryRights[];
  showLinkedCompanies?: boolean;
  redirectTo?: string;
}

const isContactAllowedToRegister = (right: ContactSignatoryRights) =>
  !right.communityUserId || right.userProfile === UserProfile.EMPLOYEE;

const CompanyNotFoundInfoElement = () => (
  <div>
    <p>
      <b>{t.O1F2('Do you have administration rights?')}</b>
    </p>
    <p>
      {t.XU1F(
        'Making orders for the company or viewing company information requires the authority to sign on behalf of the company or administration rights in OmaElisa for Companies. If necessary, ask the company administrator to give you the necessary rights in OmaElisa for Companies.'
      )}
    </p>
    <p>
      <b>{t.GW69('Is your company new?')}</b>
    </p>
    <p>
      {t.EW6G(
        'New companies are updated to our register automatically within a few days. If you need to take care of urgent matters, contact our '
      )}
      <a href="https://yrityksille.elisa.fi/yhteystiedot">{t.MMUE('customer service')}</a>.
    </p>
    <p>
      <b>{t.GCJB('Are you looking for OmaElisa for Employees?')}</b>
    </p>
    <p>
      {t.WIZR('If you are an employee, you can manage your corporate subscriptions and devices in ')}
      <a href={paths.EMPLOYEE_HOME}>{t.ZPZR(employeeOmaElisaMsg)}.</a>
    </p>
  </div>
);

const NoSignatoryRightsFound = ({
  isExistingUser,
  onGoToOmaElisa,
}: {
  isExistingUser: boolean;
  onGoToOmaElisa: () => void;
}) => (
  <div className="of-select-signatory-right__body">
    <h2 className={isExistingUser ? dsClass.MARGIN_VERTICAL_0 : dsClass.PADDING_BOTTOM_3}>
      {t.H5KU(`We couldn't find any companies`)}
    </h2>
    {isExistingUser && (
      <div className={dsClass.PADDING_VERTICAL_3}>
        <p>
          {t.FPE7(
            `We didn't find any new companies to add from your information. Companies already added to your Elisa ID account can be found in Corporate OmaElisa.`
          )}
        </p>
        <CL.Button color="light" onClick={onGoToOmaElisa}>
          {t.Z13H(goToOmaElisaMsg)}
        </CL.Button>
      </div>
    )}
    <CompanyNotFoundInfoElement />
  </div>
);

interface SignatoryRightsCardsProps {
  contactSignatoryRights: ContactSignatoryRights[];
  selectCompanySignatoryRight: (right: ContactSignatoryRights) => void;
  hideCards: boolean;
}

const SignatoryRightsCards = ({
  contactSignatoryRights,
  selectCompanySignatoryRight,
  hideCards,
}: SignatoryRightsCardsProps) => {
  if (hideCards) {
    return null;
  }
  return (
    <div className={`${dsClass.MARGIN_TOP_5} ${dsClass.WIDTH_100}`}>
      {contactSignatoryRights.map((signatoryRight: ContactSignatoryRights) => (
        <div key={signatoryRight.accountMDMID}>
          <CL.GridRow className={`${dsClass.PADDING_BOTTOM_4}`}>
            <CL.Card
              content={
                !isContactAllowedToRegister(signatoryRight) ? (
                  <div className={`of-select-signatory-right__linked-card ${dsClass.DISPLAY_FLEX}`}>
                    <div
                      className={`of-select-signatory-right__card-row ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
                    >
                      <h4>
                        {`${signatoryRight.accountName}${signatoryRight.businessId ? ` | ${signatoryRight.businessId}` : ''}`}
                      </h4>
                      <CL.Button
                        color="primary"
                        className="of-select-signatory-right__nowrap-button"
                        onClick={() => selectCompanySignatoryRight(signatoryRight)}
                      >
                        {t.QRYV(selectMsg)}
                      </CL.Button>
                    </div>
                    <div
                      className={`of-select-signatory-right__linked-indicator ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
                    >
                      <span className="of-select-signatory-right__linked-dot" />
                      <span>{t.AE6V(companyLinkedToElisaIdMsg)}</span>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`of-select-signatory-right__card-row ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
                  >
                    <h4>
                      {signatoryRight.accountName +
                        (signatoryRight.businessId ? ` | ${signatoryRight.businessId}` : '')}
                    </h4>
                    <CL.Button
                      color="secondary"
                      className="of-select-signatory-right__nowrap-button"
                      onClick={() => selectCompanySignatoryRight(signatoryRight)}
                    >
                      {t.ZZWP(attachToAccountMsg)}
                    </CL.Button>
                  </div>
                )
              }
            />
          </CL.GridRow>
        </div>
      ))}
    </div>
  );
};

interface SignatoryRightsDropDownProps {
  contactSignatoryRights: ContactSignatoryRights[];
  selectCompanySignatoryRight: (right?: ContactSignatoryRights) => void;
  selectedSignatoryRight?: ContactSignatoryRights;
}

const SignatoryRightsDropDown = ({
  contactSignatoryRights,
  selectCompanySignatoryRight,
  selectedSignatoryRight,
}: SignatoryRightsDropDownProps) => {
  const companyNamesWithBusinessIds = contactSignatoryRights
    .map(right => {
      const label = right.accountName + (right.businessId ? ` | ${right.businessId}` : '');
      if (!isContactAllowedToRegister(right)) {
        return {
          label,
          value: right.accountMDMID,
          html: (
            <div
              className={`of-select-signatory-right__dropdown-item ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.JUSTIFY_CONTENT_SPACE_BETWEEN} ${dsClass.WIDTH_100}`}
            >
              <span>{label}</span>
              <span
                className={`of-select-signatory-right__dropdown-linked ${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.COLOR_BLUE_800}`}
              >
                <span className="of-select-signatory-right__linked-dot" />
                {t.ZZWQ(linkedMsg)}
              </span>
            </div>
          ),
        };
      }
      return { label, value: right.accountMDMID };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const itemSelected = (value?: string) => {
    const selectedRight = contactSignatoryRights.find(right => right.accountMDMID === value);
    selectCompanySignatoryRight(selectedRight);
  };

  return (
    <Combobox
      className={`${dsClass.MARGIN_0}`}
      items={companyNamesWithBusinessIds}
      label={t.KJTS(companyMsg)}
      onValueSelect={value => itemSelected(value)}
      selectedValue={selectedSignatoryRight?.accountMDMID}
      selectedItemFormat="label"
      i18n_combobox_placeholderText={t.O3ZI('Search using Business ID or Company Name')}
    />
  );
};

interface SelectedSignatoryRightProps {
  signatoryRight?: ContactSignatoryRights;
  confirmSelection: (email: string, phoneNumber: string) => void;
  cancelSelection: () => void;
}

const SelectedSignatoryRightSubmitForm = ({
  signatoryRight,
  confirmSelection,
  cancelSelection,
}: SelectedSignatoryRightProps) => {
  const methods = useForm({
    mode: 'all',
    defaultValues: {
      email: '',
      phoneNumber: '',
    },
  });
  const submitSelectedSignatoryRight = async (values: { email: string; phoneNumber: string }) => {
    confirmSelection(values.email, values.phoneNumber);
  };

  if (signatoryRight) {
    return (
      <div
        className={`${dsClass.PADDING_5} ${dsClass.BACKGROUND_COLOR_WHITE} of-select-signatory-right__form_container`}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(submitSelectedSignatoryRight)} noValidate>
            <FormGridFieldset
              legend={`${signatoryRight.accountName}${signatoryRight.businessId ? ` | ${signatoryRight.businessId}` : ''}`}
            >
              <div className={`${FORM_GRID_FULL_WIDTH} ${dsClass.PADDING_BOTTOM_3}`}>
                {t.EL7L('Enter your corporate contact details')}
              </div>
              <Email label={t.R9VH('Email address (work)')} readonly={false} />
              <PhoneNumber label={t.DIRC('Phone number (work)')} readonly={false} />
              <div className={`${FORM_GRID_FORCE_END} ${dsClass.TEXT_ALIGN_END}`}>
                <CL.Button color="link" size="m" onClick={cancelSelection}>
                  {t.B2V1('Cancel')}
                </CL.Button>
                <CL.Button type="submit" size="m">
                  {t.PWWO('Confirm and continue')}
                </CL.Button>
              </div>
            </FormGridFieldset>
          </form>
        </FormProvider>
      </div>
    );
  }
  return null;
};

interface SignatoryRightsSelectionProps {
  contactSignatoryRights: ContactSignatoryRights[];
  selectCompanySignatoryRight: (right?: ContactSignatoryRights) => void;
  selectedSignatoryRight?: ContactSignatoryRights;
  confirmSelection: (email: string, phoneNumber: string) => void;
  cancelSelection: () => void;
}

const SignatoryRightsSelectionWithCards = ({
  contactSignatoryRights,
  selectCompanySignatoryRight,
  selectedSignatoryRight,
  confirmSelection,
  cancelSelection,
}: SignatoryRightsSelectionProps) => (
  <>
    <SignatoryRightsCards
      contactSignatoryRights={contactSignatoryRights}
      selectCompanySignatoryRight={selectCompanySignatoryRight}
      hideCards={!!selectedSignatoryRight}
    />
    <SelectedSignatoryRightSubmitForm
      signatoryRight={selectedSignatoryRight}
      confirmSelection={confirmSelection}
      cancelSelection={cancelSelection}
    />
  </>
);

const SignatoryRightsSelectionWithDropDown = ({
  contactSignatoryRights,
  selectCompanySignatoryRight,
  selectedSignatoryRight,
  confirmSelection,
  cancelSelection,
}: SignatoryRightsSelectionProps) => (
  <div className={`${dsClass.WIDTH_100} ${dsClass.PADDING_3} ${dsClass.BACKGROUND_COLOR_BLUE_100}`}>
    <SignatoryRightsDropDown
      contactSignatoryRights={contactSignatoryRights}
      selectCompanySignatoryRight={selectCompanySignatoryRight}
      selectedSignatoryRight={selectedSignatoryRight}
    />
    <div className={`${selectedSignatoryRight ? dsClass.MARGIN_TOP_3 : ''}`}>
      <SelectedSignatoryRightSubmitForm
        signatoryRight={selectedSignatoryRight}
        confirmSelection={confirmSelection}
        cancelSelection={cancelSelection}
      />
    </div>
  </div>
);

const SignatoryRightsHeader = () => (
  <>
    <CL.GridRow>
      <h2 className={dsClass.MARGIN_VERTICAL_0}>{t.P3C8(companySelectMsg)}</h2>
    </CL.GridRow>
    <CL.GridRow className={dsClass.MARGIN_VERTICAL_3}>
      {t.QUBS('To manage your company’s matters in OmaElisa for Companies, attach your Elisa ID to the company.')}
    </CL.GridRow>
  </>
);

const SignatoryRightInfoAccordion = ({ hideAccordion }: { hideAccordion: boolean }) => {
  if (hideAccordion) {
    return null;
  }
  return (
    <CL.GridRow>
      <CL.GridCol colWidth={12} className={dsClass.PADDING_0}>
        <CL.Accordion>
          <CL.AccordionItem heading={t.XFMF('Is your company not on the list?')}>
            <CompanyNotFoundInfoElement />
          </CL.AccordionItem>
        </CL.Accordion>
      </CL.GridCol>
    </CL.GridRow>
  );
};

const SignatoryRightsFooter = () => (
  <CL.GridRow>
    <CL.GridCol colWidth={12} className={dsClass.PADDING_0}>
      <p className={`${dsClass.TEXT_S}`}>{t.V9OQ(informantBISMsg)}</p>
    </CL.GridCol>
  </CL.GridRow>
);

const SignatoryRightsRegistering = () => (
  <div className="of-select-signatory-right__registering">
    <Loading big={true} />
    <h4 className={dsClass.MARGIN_VERTICAL_0}>{t.NC8H('Saving')}</h4>
    <p className={`${dsClass.TEXT_S} ${dsClass.MARGIN_TOP_2}`}>{t.QB1A('Saving your information...')}</p>
  </div>
);

export const SelectCompanySignatoryRight = ({
  contactSignatoryRights,
  showLinkedCompanies = false,
  redirectTo,
}: SelectCompanySignatoryRightProps) => {
  const [userRegisterErrorDialogVisible, setUserRegisterErrorDialogVisible] = useState(false);
  const [selectedSignatoryRight, setSelectedSignatoryRight] = useState<ContactSignatoryRights>();
  const [registering, setRegistering] = useState(false);
  const { logout, validateAuth, authenticatedUser, prepareElisaIdLogin } = useAuth();
  const navigate = useNavigate();
  const triggerLogout = () => {
    logout(paths.PUBLIC_HOME);
  };
  const goToOmaElisa = () => {
    prepareElisaIdLogin({ callPath: paths.SELF_SERVICE_HOME, changeContext: true });
  };

  useEffect(() => {
    if (authenticatedUser?.businessId) {
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else {
        navigate(paths.SELF_SERVICE_HOME, { replace: true });
      }
    }
  }, [authenticatedUser, navigate, redirectTo]);

  const callRegisterUser = async (email: string, phoneNumber: string) => {
    setRegistering(true);
    try {
      await registerIndividual({ accountMDMID: selectedSignatoryRight!.accountMDMID, email, phoneNumber });
      validateAuth();
    } catch (error) {
      setRegistering(false);
      setUserRegisterErrorDialogVisible(true);
    }
  };

  const callLoginWithCommunityUser = async (right: ContactSignatoryRights) => {
    setRegistering(true);
    try {
      await loginWithAccountCommunityUser(right.accountMDMID);
      validateAuth();
    } catch (error) {
      setRegistering(false);
      setUserRegisterErrorDialogVisible(true);
    }
  };

  const handleSelectCompanySignatoryRight = (right?: ContactSignatoryRights) => {
    if (right && !isContactAllowedToRegister(right)) {
      callLoginWithCommunityUser(right);
    } else {
      setSelectedSignatoryRight(right);
    }
  };

  const filteredSignatoryRights = showLinkedCompanies
    ? contactSignatoryRights
    : contactSignatoryRights.filter(isContactAllowedToRegister);

  if (!filteredSignatoryRights || filteredSignatoryRights.length === 0) {
    return (
      <div className={`${dsClass.PADDING_VERTICAL_5} of-select-signatory-right__root`}>
        <NoSignatoryRightsFound
          isExistingUser={contactSignatoryRights.length > filteredSignatoryRights.length}
          onGoToOmaElisa={goToOmaElisa}
        />
      </div>
    );
  }

  return !userRegisterErrorDialogVisible ? (
    <div className={`${dsClass.PADDING_VERTICAL_5} of-select-signatory-right__root`}>
      <CL.Grid className="of-select-signatory-right__body">
        <SignatoryRightsHeader />
        {registering ? (
          <SignatoryRightsRegistering />
        ) : filteredSignatoryRights.length > 4 ? (
          <SignatoryRightsSelectionWithDropDown
            contactSignatoryRights={filteredSignatoryRights}
            selectCompanySignatoryRight={handleSelectCompanySignatoryRight}
            selectedSignatoryRight={selectedSignatoryRight}
            confirmSelection={callRegisterUser}
            cancelSelection={() => setSelectedSignatoryRight(undefined)}
          />
        ) : (
          <SignatoryRightsSelectionWithCards
            contactSignatoryRights={filteredSignatoryRights}
            selectCompanySignatoryRight={handleSelectCompanySignatoryRight}
            selectedSignatoryRight={selectedSignatoryRight}
            confirmSelection={callRegisterUser}
            cancelSelection={() => setSelectedSignatoryRight(undefined)}
          />
        )}
        <SignatoryRightInfoAccordion hideAccordion={!!selectedSignatoryRight} />
        <SignatoryRightsFooter />
      </CL.Grid>
    </div>
  ) : (
    <GenericInfoDialog
      onCloseDialog={() => {
        setUserRegisterErrorDialogVisible(false);
        triggerLogout();
      }}
      header={t.DDU5('Registration failed')}
      body={
        <div>{t.UC8X(`Please try again and if the issue continues, please contact Elisa's customer service.`)}</div>
      }
    />
  );
};
